import { supabase } from '../../supabase/client';
import { VacationDay } from '../../../types/schedule';
import { PostgrestError } from '@supabase/supabase-js';

// Check if supabase client is available
if (!supabase) {
  throw new Error('Supabase client not initialized');
}

// Define the return type for vacation day operations
interface VacationDayOperationResult {
  data: { vacation_days: VacationDay[] } | null;
  error: PostgrestError | null;
}

// Note: For simplicity, we're storing vacation days in a JSONB field in the schedules table
// In a more complex implementation, you might use a separate vacations table

export const vacationService = {
  /**
   * Add vacation days to a schedule
   */
  async addVacationDays(
    scheduleId: string, 
    vacationDays: VacationDay[]
  ): Promise<VacationDayOperationResult> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    console.log('Adding vacation days:', JSON.stringify(vacationDays, null, 2));
    
    // First, get the current vacation days
    const { data: schedule, error: fetchError } = await supabase
      .from('schedules')
      .select('vacation_days')
      .eq('id', scheduleId)
      .single();
    
    if (fetchError) {
      console.error('Error fetching schedule:', fetchError);
      return { data: null, error: fetchError };
    }

    // Merge existing and new vacation days
    const existingDays = schedule?.vacation_days || [];
    console.log('Existing vacation days:', JSON.stringify(existingDays, null, 2));
    
    const mergedDays = [...existingDays, ...vacationDays];
    console.log('Merged vacation days:', JSON.stringify(mergedDays, null, 2));
    
    // Update the schedule with the merged vacation days
    const { data, error } = await supabase
      .from('schedules')
      .update({ vacation_days: mergedDays })
      .eq('id', scheduleId)
      .select('vacation_days')
      .single();
    
    if (error) {
      console.error('Error updating vacation days:', error);
    } else {
      console.log('Successfully updated vacation days:', data);
    }
    
    return { data, error };
  },

  /**
   * Remove a vacation day
   */
  async removeVacationDay(
    scheduleId: string, 
    vacationDayToRemove: VacationDay
  ): Promise<VacationDayOperationResult> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    // First, get the current vacation days
    const { data: schedule, error: fetchError } = await supabase
      .from('schedules')
      .select('vacation_days')
      .eq('id', scheduleId)
      .single();
    
    if (fetchError) {
      return { data: null, error: fetchError };
    }

    // Filter out the vacation day to remove
    const existingDays = schedule?.vacation_days || [];
    const filteredDays = existingDays.filter(
      (day: VacationDay) => 
        !(day.providerId === vacationDayToRemove.providerId && 
          day.date === vacationDayToRemove.date)
    );
    
    // Update the schedule with the filtered vacation days
    const { data, error } = await supabase
      .from('schedules')
      .update({ vacation_days: filteredDays })
      .eq('id', scheduleId)
      .select('vacation_days')
      .single();
    
    return { data, error };
  },

  /**
   * Get all vacation days for a schedule
   */
  async getVacationDays(scheduleId: string): Promise<{ data: VacationDay[] | null; error: PostgrestError | null }> {
    if (!supabase) {
      return { data: null, error: new Error('Supabase client not initialized') as PostgrestError };
    }
    
    const { data, error } = await supabase
      .from('schedules')
      .select('vacation_days')
      .eq('id', scheduleId)
      .single();
    
    console.log('Retrieved vacation days:', data?.vacation_days);
    
    return { 
      data: data?.vacation_days || [], 
      error 
    };
  }
}; 